import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface Window {
    __pwaInstallPrompt?: BeforeInstallPromptEvent | null;
  }
}

function detectBrowser(): 'chrome' | 'firefox' | 'safari' | 'samsung' | 'other' {
  const ua = navigator.userAgent;
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/CriOS|Chrome/i.test(ua) && !/Edge|Edg/i.test(ua)) return 'chrome';
  if (/Firefox|FxiOS/i.test(ua)) return 'firefox';
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'safari';
  return 'other';
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const browser = detectBrowser();
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Pick up prompt captured before React mounted
    if (window.__pwaInstallPrompt) {
      setDeferredPrompt(window.__pwaInstallPrompt);
      window.__pwaInstallPrompt = null;
    }

    function handleBeforeInstall(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }

    function handleAppInstalled() {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowInstructions(false);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      // No native prompt available — show manual instructions
      setShowInstructions(true);
    }
  }, [deferredPrompt]);

  const getInstructions = useCallback((): { title: string; steps: string[] } => {
    if (isIos) {
      return {
        title: 'Instalar no iOS (Safari)',
        steps: [
          'Toque no botão de compartilhamento (ícone de quadrado com seta para cima)',
          'Role para baixo e toque em "Adicionar à Tela de Início"',
          'Toque em "Adicionar" para confirmar',
        ],
      };
    }
    switch (browser) {
      case 'chrome':
        return {
          title: 'Instalar no Chrome',
          steps: [
            'Toque no menu (⋮) no canto superior direito',
            'Toque em "Instalar aplicativo" ou "Adicionar à tela inicial"',
            'Toque em "Instalar" para confirmar',
          ],
        };
      case 'samsung':
        return {
          title: 'Instalar no Samsung Internet',
          steps: [
            'Toque no menu (≡) na parte inferior',
            'Toque em "Adicionar à tela inicial"',
            'Toque em "Adicionar" para confirmar',
          ],
        };
      case 'firefox':
        return {
          title: 'Instalar no Firefox',
          steps: [
            'Toque no menu (⋮) no canto superior direito',
            'Toque em "Instalar"',
            'Toque em "Adicionar" para confirmar',
          ],
        };
      default:
        return {
          title: 'Instalar aplicativo',
          steps: [
            'Abra o menu do navegador',
            'Procure por "Instalar" ou "Adicionar à tela inicial"',
            'Confirme a instalação',
          ],
        };
    }
  }, [browser, isIos]);

  return {
    canShow: !isInstalled,
    hasNativePrompt: !!deferredPrompt,
    isInstalled,
    install,
    showInstructions,
    setShowInstructions,
    getInstructions,
  };
}
